// @ts-check
//
// Client-side helpers for the Provider tier (issue #201).
//
// Reads go through `from("providers")` (RLS limits the caller to their own
// row); writes (create/approve/revoke) go through the dedicated edge
// functions. The full API key secret never appears anywhere outside the
// 201 response from `createProviderApiKey` — subsequent listings only
// surface the `key_prefix`.

import { from } from "./backend/db";
import { functions as backendFunctions } from "./backend/functions";

export const PROVIDER_SCOPES = [
  "read:orgs",
  "write:orgs",
  "write:events",
  "write:ticket_types",
  "read:widgets",
  "read:tickets",
  "refund:tickets",
  "manage:members",
];

const SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,79}$/;

export function slugifyProviderName(name) {
  const base = String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  if (!base) return "";
  // Append a 4-char suffix so two providers can pick the same display name
  // without colliding on the unique slug index.
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
}

/**
 * Look up the provider record owned by the current authenticated user.
 * Returns `{ data: null }` when the user is not a provider yet.
 */
export async function fetchMyProvider({ userId }) {
  if (!userId) return { data: null, error: null };
  return from("providers")
    .select(
      "id, slug, name, contact_email, approval_status, created_at, approved_at, rejected_at"
    )
    .eq("created_by_user_id", userId)
    .maybeSingle();
}

/**
 * Insert the provider row tied to the current user. The migration's RLS
 * policy enforces `created_by_user_id = auth.uid()`, so the JWT path is
 * what authorizes the write.
 */
export async function registerProvider({ userId, name, contactEmail }) {
  if (!userId) {
    return { data: null, error: new Error("missing userId") };
  }
  const trimmedName = String(name || "").trim();
  if (!trimmedName) {
    return { data: null, error: new Error("name is required") };
  }
  let slug = slugifyProviderName(trimmedName);
  if (!slug || !SLUG_RE.test(slug)) {
    return { data: null, error: new Error("could not generate slug") };
  }
  const row = {
    name: trimmedName,
    contact_email: String(contactEmail || "").trim().toLowerCase(),
    slug,
    approval_status: "pending",
    created_by_user_id: userId,
  };
  return from("providers").insert(row);
}

/**
 * Active (non-revoked) API key list for the calling provider. We never
 * select `key_hash`; the column is filtered out at RLS time too, but
 * keeping the select list explicit avoids accidental over-fetching if a
 * future migration relaxes that policy.
 */
export async function listProviderApiKeys({ providerId }) {
  if (!providerId) return { data: [], error: null };
  return from("provider_api_keys")
    .select("id, name, key_prefix, scopes_json, last_used_at, created_at, revoked_at")
    .eq("provider_id", providerId)
    .order("created_at", { ascending: false });
}

/**
 * Mint a new API key. Returns the full secret in `data.secret`. The caller
 * is responsible for displaying it ONCE and discarding the value
 * afterwards — no other endpoint exposes it again.
 */
export async function createProviderApiKey({ name, scopes }) {
  return backendFunctions.createProviderApiKey({ name, scopes });
}

export async function revokeProviderApiKey({ keyId }) {
  return backendFunctions.revokeProviderApiKey({ keyId });
}

/**
 * Organisations owned by the provider. `provider_owned_orgs` is the join
 * table; `organizations` lands with PR #206 (issue #194). Until then, the
 * list is empty in practice — but the query is forward-compatible.
 */
export async function listProviderOwnedOrgs({ providerId }) {
  if (!providerId) return { data: [], error: null };
  return from("provider_owned_orgs")
    .select("id, organization_id, created_at")
    .eq("provider_id", providerId)
    .order("created_at", { ascending: false });
}
